import * as React from 'react'
import { Check, ChevronDown, Clock } from 'lucide-react'
import { Button } from '@/components/ui/button'
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from '@/components/ui/command'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import { cn } from '@/lib/utils'
import { DateTime } from 'luxon'

// Timezone mapping: Enum values (UPPERCASE_UNDERSCORE) to IANA timezone identifiers
const TIMEZONE_MAPPING = {
  // Top 4 US timezones as requested
  AMERICA_NEW_YORK: 'America/New_York', // Eastern Time
  AMERICA_CHICAGO: 'America/Chicago', // Central Time
  AMERICA_DENVER: 'America/Denver', // Mountain Time
  AMERICA_LOS_ANGELES: 'America/Los_Angeles', // Pacific Time

  // US Additional
  AMERICA_PHOENIX: 'America/Phoenix',
  AMERICA_ANCHORAGE: 'America/Anchorage',
  PACIFIC_HONOLULU: 'Pacific/Honolulu',

  // Canada
  AMERICA_TORONTO: 'America/Toronto',
  AMERICA_VANCOUVER: 'America/Vancouver',
  AMERICA_MONTREAL: 'America/Montreal',

  // Europe
  EUROPE_LONDON: 'Europe/London',
  EUROPE_PARIS: 'Europe/Paris',
  EUROPE_BERLIN: 'Europe/Berlin',
  EUROPE_ROME: 'Europe/Rome',
  EUROPE_MADRID: 'Europe/Madrid',
  EUROPE_AMSTERDAM: 'Europe/Amsterdam',
  EUROPE_STOCKHOLM: 'Europe/Stockholm',
  EUROPE_ZURICH: 'Europe/Zurich',

  // Asia Pacific
  ASIA_TOKYO: 'Asia/Tokyo',
  ASIA_SHANGHAI: 'Asia/Shanghai',
  ASIA_HONG_KONG: 'Asia/Hong_Kong',
  ASIA_SINGAPORE: 'Asia/Singapore',
  ASIA_DUBAI: 'Asia/Dubai',
  ASIA_KOLKATA: 'Asia/Kolkata',

  // Australia/Oceania
  AUSTRALIA_SYDNEY: 'Australia/Sydney',
  AUSTRALIA_MELBOURNE: 'Australia/Melbourne',
  PACIFIC_AUCKLAND: 'Pacific/Auckland',

  // South America
  AMERICA_SAO_PAULO: 'America/Sao_Paulo',
  AMERICA_BUENOS_AIRES: 'America/Buenos_Aires',

  // Africa
  AFRICA_JOHANNESBURG: 'Africa/Johannesburg',
  AFRICA_CAIRO: 'Africa/Cairo',
} as const

export type TimezoneEnumValue = keyof typeof TIMEZONE_MAPPING

// Comprehensive timezone aliases and search terms
const TIMEZONE_ALIASES: Record<TimezoneEnumValue, string[]> = {
  // US Priority Timezones
  AMERICA_NEW_YORK: [
    'eastern',
    'est',
    'edt',
    'eastern standard time',
    'eastern daylight time',
    'new york',
  ],
  AMERICA_CHICAGO: [
    'central',
    'cst',
    'cdt',
    'central standard time',
    'central daylight time',
    'chicago',
  ],
  AMERICA_DENVER: [
    'mountain',
    'mst',
    'mdt',
    'mountain standard time',
    'mountain daylight time',
    'denver',
  ],
  AMERICA_LOS_ANGELES: [
    'pacific',
    'pst',
    'pdt',
    'pacific standard time',
    'pacific daylight time',
    'los angeles',
    'california',
  ],

  // US Additional
  AMERICA_PHOENIX: ['arizona', 'phoenix', 'mst', 'mountain standard time'],
  AMERICA_ANCHORAGE: [
    'alaska',
    'akst',
    'akdt',
    'alaska standard time',
    'alaska daylight time',
    'anchorage',
  ],
  PACIFIC_HONOLULU: ['hawaii', 'hst', 'hawaii standard time', 'honolulu'],

  // Canada
  AMERICA_TORONTO: ['toronto', 'eastern', 'est', 'edt', 'ontario'],
  AMERICA_VANCOUVER: ['vancouver', 'pacific', 'pst', 'pdt', 'british columbia'],
  AMERICA_MONTREAL: ['montreal', 'eastern', 'est', 'edt', 'quebec'],

  // Europe
  EUROPE_LONDON: [
    'london',
    'gmt',
    'bst',
    'greenwich mean time',
    'british summer time',
    'uk',
    'britain',
  ],
  EUROPE_PARIS: [
    'paris',
    'cet',
    'cest',
    'central european time',
    'central european summer time',
    'france',
  ],
  EUROPE_BERLIN: [
    'berlin',
    'cet',
    'cest',
    'central european time',
    'central european summer time',
    'germany',
  ],
  EUROPE_ROME: [
    'rome',
    'cet',
    'cest',
    'central european time',
    'central european summer time',
    'italy',
  ],
  EUROPE_MADRID: [
    'madrid',
    'cet',
    'cest',
    'central european time',
    'central european summer time',
    'spain',
  ],
  EUROPE_AMSTERDAM: [
    'amsterdam',
    'cet',
    'cest',
    'central european time',
    'central european summer time',
    'netherlands',
  ],
  EUROPE_STOCKHOLM: [
    'stockholm',
    'cet',
    'cest',
    'central european time',
    'central european summer time',
    'sweden',
  ],
  EUROPE_ZURICH: [
    'zurich',
    'cet',
    'cest',
    'central european time',
    'central european summer time',
    'switzerland',
  ],

  // Asia Pacific
  ASIA_TOKYO: ['tokyo', 'jst', 'japan standard time', 'japan'],
  ASIA_SHANGHAI: ['shanghai', 'cst', 'china standard time', 'beijing', 'china'],
  ASIA_HONG_KONG: ['hong kong', 'hkt', 'hong kong time'],
  ASIA_SINGAPORE: ['singapore', 'sgt', 'singapore standard time'],
  ASIA_DUBAI: ['dubai', 'gst', 'gulf standard time', 'uae'],
  ASIA_KOLKATA: ['kolkata', 'mumbai', 'ist', 'india standard time', 'india'],

  // Australia/Oceania
  AUSTRALIA_SYDNEY: [
    'sydney',
    'aest',
    'aedt',
    'australian eastern standard time',
    'australian eastern daylight time',
    'australia',
  ],
  AUSTRALIA_MELBOURNE: [
    'melbourne',
    'aest',
    'aedt',
    'australian eastern standard time',
    'australian eastern daylight time',
    'australia',
  ],
  PACIFIC_AUCKLAND: [
    'auckland',
    'nzst',
    'nzdt',
    'new zealand standard time',
    'new zealand daylight time',
    'new zealand',
  ],

  // South America
  AMERICA_SAO_PAULO: [
    'sao paulo',
    'são paulo',
    'brt',
    'brst',
    'brazil time',
    'brasilia time',
    'brazil',
  ],
  AMERICA_BUENOS_AIRES: ['buenos aires', 'art', 'argentina time', 'argentina'],

  // Africa
  AFRICA_JOHANNESBURG: [
    'johannesburg',
    'sast',
    'south africa standard time',
    'south africa',
  ],
  AFRICA_CAIRO: [
    'cairo',
    'eet',
    'eest',
    'eastern european time',
    'eastern european summer time',
    'egypt',
  ],
}

// Top 4 US timezones as requested
const US_PRIORITY_TIMEZONES: readonly TimezoneEnumValue[] = [
  'AMERICA_NEW_YORK',
  'AMERICA_CHICAGO',
  'AMERICA_DENVER',
  'AMERICA_LOS_ANGELES',
] as const

// Additional common timezones organized by region
const ADDITIONAL_TIMEZONES: readonly TimezoneEnumValue[] = [
  'AMERICA_PHOENIX',
  'AMERICA_ANCHORAGE',
  'PACIFIC_HONOLULU',
  'AMERICA_TORONTO',
  'AMERICA_VANCOUVER',
  'AMERICA_MONTREAL',
  'EUROPE_LONDON',
  'EUROPE_PARIS',
  'EUROPE_BERLIN',
  'EUROPE_ROME',
  'EUROPE_MADRID',
  'EUROPE_AMSTERDAM',
  'EUROPE_STOCKHOLM',
  'EUROPE_ZURICH',
  'ASIA_TOKYO',
  'ASIA_SHANGHAI',
  'ASIA_HONG_KONG',
  'ASIA_SINGAPORE',
  'ASIA_DUBAI',
  'ASIA_KOLKATA',
  'AUSTRALIA_SYDNEY',
  'AUSTRALIA_MELBOURNE',
  'PACIFIC_AUCKLAND',
  'AMERICA_SAO_PAULO',
  'AMERICA_BUENOS_AIRES',
  'AFRICA_JOHANNESBURG',
  'AFRICA_CAIRO',
] as const

interface TimezoneInfo {
  timezoneEnumValue: TimezoneEnumValue // UPPERCASE_UNDERSCORE format for database enum
  value: string // IANA timezone identifier for Luxon
  city: string
  region: string
  offset: string
  offsetMinutes: number
  abbreviation: string
  label: string
  zoneName: string
  offsetName: string
}

function getTimezoneInfo(timezoneEnumValue: TimezoneEnumValue): TimezoneInfo {
  const timezone = TIMEZONE_MAPPING[timezoneEnumValue]

  if (!timezone) {
    // Fallback for unknown enum values
    return {
      timezoneEnumValue,
      value: 'UTC',
      city: timezoneEnumValue.replace(/_/g, ' '),
      region: '',
      offset: 'UTC',
      offsetMinutes: 0,
      abbreviation: 'UTC',
      label: timezoneEnumValue.replace(/_/g, ' '),
      zoneName: 'UTC',
      offsetName: 'Coordinated Universal Time',
    }
  }

  try {
    const now = DateTime.now().setZone(timezone)

    // Check if timezone is valid
    if (!now.isValid) {
      throw new Error(`Invalid timezone: ${timezone}`)
    }

    // Extract city and region from timezone string
    const parts = timezone.split('/')
    const region = parts[0] || ''
    const city = parts[1]?.replace(/_/g, ' ') || timezone

    // Get offset information using Luxon
    const offset = now.toFormat('ZZ') // e.g., "-05:00"
    const offsetMinutes = now.offset

    // Try to get abbreviation from Luxon, fallback to our mapping
    let abbreviation = now.toFormat('ZZZZ') // e.g., "PST", "EST", "CST"
    let offsetName = now.toFormat('ZZZZZ') // Long offset name like "Pacific Standard Time"

    // If Luxon doesn't provide good values, use our comprehensive mapping
    const aliases = TIMEZONE_ALIASES[timezoneEnumValue] || []
    const abbreviationAlias = aliases.find(
      alias => alias.length === 3 && alias.match(/^[A-Z]{3}$/i),
    )
    const fullNameAlias = aliases.find(
      alias => alias.toLowerCase().includes('time') && alias.length > 10,
    )

    // Use better values if available - prefer our mapping for better consistency
    if (abbreviationAlias) {
      abbreviation = abbreviationAlias.toUpperCase()
    }

    if (
      fullNameAlias &&
      (!offsetName || offsetName === timezone || offsetName.startsWith('+'))
    ) {
      offsetName = fullNameAlias
    }

    // Get additional timezone names from Luxon
    const { zoneName } = now // Full zone name

    // Create a more user-friendly offset display
    const gmtOffset =
      offsetMinutes === 0
        ? 'GMT'
        : offsetMinutes > 0
          ? `GMT+${Math.floor(offsetMinutes / 60)}`
          : `GMT${Math.floor(offsetMinutes / 60)}`

    return {
      timezoneEnumValue,
      value: timezone,
      city,
      region,
      offset,
      offsetMinutes,
      abbreviation,
      label: `${city} (${abbreviation || gmtOffset})`,
      zoneName,
      offsetName,
    }
  } catch (error) {
    // Fallback for invalid timezones
    const parts = timezone.split('/')
    const city = parts[1]?.replace(/_/g, ' ') || timezone

    return {
      timezoneEnumValue,
      value: timezone,
      city,
      region: parts[0] || '',
      offset: 'UTC',
      offsetMinutes: 0,
      abbreviation: 'UTC',
      label: city,
      zoneName: timezone,
      offsetName: 'Coordinated Universal Time',
    }
  }
}

interface TimezoneSelectorProps {
  value?: TimezoneEnumValue | '' | null
  onValueChange?: (value: TimezoneEnumValue) => void
  placeholder?: string
  disabled?: boolean
}

export function TimezoneSelector({
  value,
  onValueChange,
  placeholder = 'Select timezone...',
  disabled = false,
}: TimezoneSelectorProps) {
  const [open, setOpen] = React.useState(false)

  // Memoize timezone processing for performance
  const { priorityTimezones, additionalTimezones } = React.useMemo(
    () => ({
      priorityTimezones: US_PRIORITY_TIMEZONES.map(getTimezoneInfo),
      additionalTimezones: ADDITIONAL_TIMEZONES.map(getTimezoneInfo),
    }),
    [],
  )

  const selectedTimezone = React.useMemo(() => {
    if (!value) return null
    return getTimezoneInfo(value)
  }, [value])

  const handleSelect = React.useCallback(
    (timezoneEnumValue: TimezoneEnumValue) => {
      onValueChange?.(timezoneEnumValue)
      setOpen(false)
    },
    [onValueChange],
  )

  // Render individual timezone item
  const renderTimezoneItem = React.useCallback(
    (timezone: TimezoneInfo) => {
      // Create searchable value that includes all aliases
      const aliases = TIMEZONE_ALIASES[timezone.timezoneEnumValue] || []
      const searchableValue = [
        timezone.city,
        timezone.value,
        timezone.abbreviation,
        ...aliases,
      ]
        .join(' ')
        .toLowerCase()

      return (
        <CommandItem
          key={timezone.timezoneEnumValue}
          value={searchableValue}
          onSelect={() => handleSelect(timezone.timezoneEnumValue)}
        >
          <Check
            className={cn(
              'mr-2 size-4',
              value === timezone.timezoneEnumValue
                ? 'opacity-100'
                : 'opacity-0',
            )}
          />
          <div className="flex flex-col min-w-0 flex-1">
            <div className="flex items-center justify-between">
              <span className="font-medium">{timezone.city}</span>
              <div className="flex items-baseline gap-2 text-xs text-muted-foreground font-mono">
                <span>{timezone.abbreviation}</span>
              </div>
            </div>
            <span className="text-xs text-muted-foreground truncate">
              {timezone.value}
            </span>
          </div>
        </CommandItem>
      )
    },
    [value, handleSelect],
  )

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild className="max-w-lg">
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className="w-full justify-between"
          disabled={disabled}
        >
          <div className="flex items-center gap-2 min-w-0">
            <Clock className="size-4 shrink-0 opacity-50" />
            <span className="truncate">
              {selectedTimezone ? (
                <span className="flex items-baseline gap-2">
                  <span className="font-medium">{selectedTimezone.city}</span>
                  <span className="text-xs text-muted-foreground">
                    {selectedTimezone.abbreviation}
                  </span>
                </span>
              ) : (
                placeholder
              )}
            </span>
          </div>
          <ChevronDown className="ml-2 size-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="max-w-lg p-0" align="start">
        <Command>
          <CommandInput
            placeholder="Search by city or timezone..."
            variant="popover"
          />
          <CommandList>
            <CommandEmpty>No timezone found.</CommandEmpty>

            <CommandGroup heading="United States">
              {priorityTimezones.map(renderTimezoneItem)}
            </CommandGroup>

            <CommandSeparator />

            <CommandGroup heading="Other Locations">
              {additionalTimezones.map(renderTimezoneItem)}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  )
}
